// src/AppEventsPanel.js — live telemetry from the app and the edge functions.
//
// This sits beside the curated issue list because the two answer different
// questions. `system_errors` is what somebody decided to track; `app_events` is
// what actually happened, written by the app and by every edge function, and
// until now the only way to read it was the Supabase SQL editor.
//
// Nothing here can edit an event. Triage adds a note and a timestamp through
// admin_triage_app_event(); the record of what happened is not ours to revise.
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, CircularProgress, Alert, IconButton, Collapse,
  TextField, InputAdornment, FormControl, InputLabel, Select, MenuItem,
  Button, Dialog, DialogTitle, DialogContent, DialogActions, Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import RefreshIcon from '@mui/icons-material/Refresh';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import DoneIcon from '@mui/icons-material/Done';
import { supabase } from './supabaseClient';

import { prettyStatus } from './labels';
const severityColor = {
  debug: 'default', info: 'info', warning: 'warning',
  error: 'error', critical: 'error',
};

const sourceLabel = {
  ios: 'iPhone app', edge: 'Server', admin: 'Admin console', metrickit: 'Crash reports',
};

/// A plain-English line for the events worth recognising on sight. Anything not
/// listed falls back to the message the event carried.
function explain(event) {
  const chain = event?.context?.unverified_claims?.certificate_chain_length;
  switch (event.event_name) {
    case 'appstore.notification':
      return 'Apple told us about a subscription change, and it was applied.';
    case 'appstore.notification.rejected':
      return chain === 3
        ? 'Refused a notification that carries a full Apple certificate chain — this may be genuine and wrongly refused. Worth reporting.'
        : 'Refused a notification that was not signed by Apple. Usually someone poking the endpoint; nothing was changed.';
    case 'appstore.notification.wrong_version':
      return 'Apple is sending Version 1 notifications. Change App Store Connect to Version 2.';
    case 'appstore.notification.unmatched':
      return 'A genuine notification arrived for a subscription with no matching row here.';
    case 'appstore.notification.revocation_unchecked':
      return "Genuine Apple notification, but Apple's revocation service could not be reached. Accepted anyway.";
    case 'appstore.purchase.rejected':
      return 'The app offered a purchase that Apple would not vouch for. No subscription was granted.';
    default:
      return null;
  }
}

function EventRow({ event, onTriaged }) {
  const [open, setOpen] = useState(false);
  const note = explain(event);
  const when = new Date(event.occurred_at);

  return (
    <>
      <TableRow hover sx={{ '& > *': { borderBottom: 'unset' }, opacity: event.triaged_at ? 0.55 : 1 }}>
        <TableCell sx={{ width: 40 }}>
          <IconButton size="small" onClick={() => setOpen(!open)} aria-label="Show details">
            {open ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          <Tooltip title={when.toISOString()}>
            <span>{when.toLocaleString()}</span>
          </Tooltip>
        </TableCell>
        <TableCell>
          <Chip size="small" label={prettyStatus(event.severity)} color={severityColor[event.severity] || 'default'} />
        </TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>{sourceLabel[event.source] || event.source}</TableCell>
        <TableCell sx={{ fontFamily: 'monospace', fontSize: 13 }}>{event.event_name}</TableCell>
        <TableCell>{event.message || <em style={{ opacity: 0.6 }}>no message</em>}</TableCell>
        <TableCell sx={{ width: 120 }}>
          {event.triaged_at
            ? <Chip size="small" label="Handled" variant="outlined" />
            : <Button size="small" startIcon={<DoneIcon />} onClick={() => onTriaged(event)}>Handled</Button>}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={7} sx={{ py: 0, borderBottom: open ? undefined : 'none' }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ py: 2 }}>
              {note && <Alert severity="info" sx={{ mb: 2 }}>{note}</Alert>}
              {event.triage_note && (
                <Typography variant="body2" sx={{ mb: 2 }}>
                  <strong>Handled:</strong> {event.triage_note}
                </Typography>
              )}
              <Typography variant="caption" color="textSecondary">Details</Typography>
              <Paper variant="outlined" sx={{ p: 1.5, mt: 0.5, bgcolor: 'action.hover' }}>
                <pre style={{ margin: 0, fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {JSON.stringify(event.context ?? {}, null, 2)}
                </pre>
              </Paper>
              {(event.app_version || event.os_version || event.device_model) && (
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
                  {[event.app_version && `App ${event.app_version}`,
                    event.build_number && `build ${event.build_number}`,
                    event.os_version, event.device_model].filter(Boolean).join(' • ')}
                </Typography>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function AppEventsPanel() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [severity, setSeverity] = useState('all');
  const [source, setSource] = useState('all');
  const [showHandled, setShowHandled] = useState(false);
  const [triaging, setTriaging] = useState(null);
  const [triageNote, setTriageNote] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: queryError } = await supabase
        .from('app_events')
        .select('*')
        .order('occurred_at', { ascending: false })
        .limit(500);
      if (queryError) throw queryError;
      setEvents(data || []);
    } catch (e) {
      setError(e.message || 'Could not load events');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const confirmTriage = async () => {
    if (!triaging) return;
    try {
      const { error: rpcError } = await supabase.rpc('admin_triage_app_event', {
        p_id: triaging.id,
        p_note: triageNote,
      });
      if (rpcError) throw rpcError;
      setTriaging(null);
      setTriageNote('');
      await load();
    } catch (e) {
      setError(e.message || 'Could not mark it handled');
    }
  };

  const visible = events.filter((e) => {
    if (!showHandled && e.triaged_at) return false;
    if (severity !== 'all' && e.severity !== severity) return false;
    if (source !== 'all' && e.source !== source) return false;
    if (search) {
      const hay = `${e.event_name} ${e.message || ''} ${JSON.stringify(e.context || {})}`.toLowerCase();
      if (!hay.includes(search.toLowerCase())) return false;
    }
    return true;
  });

  const needingAttention = events.filter(
    (e) => !e.triaged_at && (e.severity === 'error' || e.severity === 'critical')).length;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="body2" color="textSecondary">
          Everything the app and the server reported, newest first.
          {needingAttention > 0 && ` ${needingAttention} needing attention.`}
        </Typography>
        <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>Refresh</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            placeholder="Search events…" size="small" value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
              endAdornment: search && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch('')}><ClearIcon fontSize="small" /></IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ flexGrow: 1, minWidth: 250 }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Severity</InputLabel>
            <Select value={severity} label="Severity" onChange={(e) => setSeverity(e.target.value)}>
              <MenuItem value="all">All severities</MenuItem>
              <MenuItem value="critical">Critical</MenuItem>
              <MenuItem value="error">Error</MenuItem>
              <MenuItem value="warning">Warning</MenuItem>
              <MenuItem value="info">Info</MenuItem>
              <MenuItem value="debug">Debug</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Where from</InputLabel>
            <Select value={source} label="Where from" onChange={(e) => setSource(e.target.value)}>
              <MenuItem value="all">Everywhere</MenuItem>
              <MenuItem value="ios">iPhone app</MenuItem>
              <MenuItem value="edge">Server</MenuItem>
              <MenuItem value="admin">Admin console</MenuItem>
              <MenuItem value="metrickit">Crash reports</MenuItem>
            </Select>
          </FormControl>
          <Button size="small" onClick={() => setShowHandled(!showHandled)}>
            {showHandled ? 'Hide handled' : 'Show handled'}
          </Button>
        </Box>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
      ) : visible.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="textSecondary">
            {events.length === 0
              ? 'No events recorded.'
              : 'Nothing matches those filters.'}
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>When</TableCell>
                <TableCell>Severity</TableCell>
                <TableCell>Where from</TableCell>
                <TableCell>Event</TableCell>
                <TableCell>What happened</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.map((e) => (
                <EventRow key={e.id} event={e} onTriaged={(ev) => { setTriaging(ev); setTriageNote(''); }} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={!!triaging} onClose={() => setTriaging(null)} fullWidth maxWidth="sm">
        <DialogTitle>Mark this handled</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            The event itself is kept exactly as recorded. This adds a note and your name.
          </Typography>
          <TextField
            autoFocus fullWidth multiline rows={3} label="What did you do about it? (optional)"
            value={triageNote} onChange={(e) => setTriageNote(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTriaging(null)}>Cancel</Button>
          <Button variant="contained" onClick={confirmTriage}>Mark handled</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
